//
// # Circuit Extraction
//

// Local imports
import * as schdata from "../schematic";
import { Ident } from "../ident";
import { Point } from "../core";
import { Result } from "../errors";

// Extract and back-annotate the circuit-level connectivity of a `Schematic`.
export function extractCircuit(sch: schdata.Schematic): Result<Extraction> {
  return new CircuitExtractor(sch).extract();
}

export interface Extraction {
  conns: schdata.Conns;
  errors: Array<ExtractionError>;
  warnings: Array<ExtractionWarning>;
}

// # Schematic to Circuit Converter State
class CircuitExtractor {
  //
  // Sole constructor argument: the input `Schematic`
  constructor(readonly sch: schdata.Schematic) {
    // Replace the schematic's `Conns` right away with a reference to our own.
    this.sch.conns = this.conns;
  }

  // Internal conversion state
  conns: schdata.Conns = new schdata.Conns();
  convSignals: Map<string, ConverterSignal> = new Map();
  numSignals: number = 0; // Number of signals created. For naming.
  errors: Array<ExtractionError> = [];
  warnings: Array<ExtractionWarning> = [];

  // Extract circuit from schematic
  extract = (): Result<Extraction, any> => {
    // Perform our main actions, handleing signals, ports, and instances
    return this.handlePins()
      .andThen(this.handleWires)
      .andThen(this.handleInstances)
      .andThen(this.finish);
  };

  // Finish up and return our schematic
  finish = (): Result<Extraction> => {
    const { conns, errors, warnings } = this;
    const extraction: Extraction = { conns, errors, warnings };

    // If we got any errors, return them wrapped with the `Err` variant.
    if (errors.length > 0) return Result.Err(extraction);

    // And if not, wrap with the `Ok` variant.
    return Result.Ok(extraction);
  };

  nextMadeUpSignalName = (): string => {
    const name = `net${this.numSignals}`;
    this.numSignals += 1;
    return name;
  };

  // Collect port-annotation objects and update their connected Signals.
  handlePins = (): Result<void> => {
    for (const pin of this.sch.pins) {
      this.handlePin(pin);
    }
    return Result.Ok(undefined);
  };

  handlePin = (pin: schdata.SchPinSpec): Result<ConverterSignal> => {
    // Check for an existing signal
    const existingSig = this.convSignals.get(pin.portName);
    if (existingSig) return Result.Ok(existingSig);

    // Create a new signal and add this wire to it
    return this.addPort(pin);
  };

  addPort = (pin: schdata.SchPinSpec): Result<ConverterSignal> => {
    const parseResult = schdata.ValidPortName.parse(pin.portName);
    if (parseResult.err) {
      const e = new InvalidPort(pin.portName);
      this.errors.push(e);
      return parseResult;
    }
    const conn: schdata.ValidPortName = parseResult.val;

    // OK so this name checks out as either a scalar signal, or a "slice" (bus).
    // Add it to our `Conns`
    this.conns.addPort(conn, pin.direction);

    // Create a new `ConverterSignal` for it
    const converterSignal = new ConverterSignal(conn);
    this.convSignals.set(pin.portName, converterSignal);
    converterSignal.schPinWrappers.push(new SchPinWrapper(pin));

    return Result.Ok(converterSignal);
  };

  handleWires = (): Result<void> => {
    // Collect wires into labeled and unlabeled sets, and collect signals for all the labeled ones
    const unlabeledWireSegmentsSet = new Set<schdata.WireSegment>();

    for (const wireSegment of this.sch.wireSegments) {
      const names = new Set<string>(
        wireSegment.labels.map((label) => label.text)
      );
      if (names.size > 1) {
        const e = new WireConflict(Array.from(names));
        this.errors.push(e);
        return this.failNow(e);
      } else if (names.size == 1) {
        // Validly named
        const name = names.values().next().value as string;
        const r = this.getOrCreateConvSignal(name);
        if (r.err) return r;
        const converterSignal = r.val;
        converterSignal.group.wireSegments.add(wireSegment);
      } else {
        // Unlabeled
        unlabeledWireSegmentsSet.add(wireSegment);
      }
    }

    // Collect un-labeled wires into connected groups
    const groups: Set<ConnectedGroup> = new Set();
    while (unlabeledWireSegmentsSet.size > 0) {
      // Pop a random `WireSegment`
      const wireSegment = setPop(unlabeledWireSegmentsSet)!;

      // Start a `Group` for it
      const group = new ConnectedGroup();
      group.wireSegments.add(wireSegment);
      groups.add(group);

      // While there are any items in the "pending connected" queue, keep merging into `group`.
      const pending: Set<schdata.WireSegment> = new Set([wireSegment]);
      while (pending.size > 0) {
        //
        const wireSegment = setPop(pending)!;
        for (const otherWire of unlabeledWireSegmentsSet.values()) {
          // If the `other` wire intersects with this one, merge it into the group.
          if (segmentsIntersect(wireSegment, otherWire)) {
            // Move it from "unlabeled" to in the group, and pending for checking its own connections.
            group.wireSegments.add(otherWire);
            pending.add(otherWire);
            unlabeledWireSegmentsSet.delete(otherWire);
          }
        }
      }
    }

    //
    // OK so now we have a set of unlabeled `ConnectedGroup`s, and another set of labeled ones.
    // Some of the unlabeled groups will be connected to labeled ones,
    // and others will need new signals created for them.
    //
    for (const group of groups.values()) {
      const connectedConvSignal = this.findSignalIntersectingGroup(group);
      if (connectedConvSignal) {
        // Merge into the existing signal
        connectedConvSignal.group.add(group);
      } else {
        // Create a new signal for this group
        const r = this.addConn(this.nextMadeUpSignalName());
        if (r.err) return r;
        const converterSignal = r.val;
        converterSignal.group = group;
      }
    }

    return Result.Ok(undefined);
  };

  handleInstances = (): Result<void> => {
    for (const inst of this.sch.instances) {
      this.handleInstance(inst);
    }
    return Result.Ok(undefined);
  };

  handleInstance = (inst: schdata.Instance): unknown => {
    // Reset the instance connections, in case it (somehow) already has some.
    inst.conns = [];

    // Get the symbol, or produce an `UnboundInstance` error
    const { symbol } = inst;
    if (!symbol) {
      // Note the instance connections are also left empty in this case.
      // Unclear what else could be done with them, as we lack port names and widths.
      const e = new UnboundInstance(inst.name, inst.target);
      return this.errors.push(e);
    }

    const handleInstancePin = (pin: schdata.SymbolPin): unknown => {
      // Transform the element-referenced port location to the instance's location
      const instPortBbox = instancePinBbox(inst, pin);

      // Check if the port intersects with any of the existing signals
      const convSignal = this.findSignalHittingBbox(instPortBbox);

      if (!convSignal) {
        const w = new UnconnectedInstancePort(inst.name, pin.portName);
        return this.warnings.push(w);
      }

      //
      // Now, handle this whole bit about repetition.
      // Some of our downstream formats really don't like to handle "broadcasting" themselves,
      // and insist code at this level figure out, e.g., that one-bit hooked up to N must be repeated N times.
      //

      // Get the width of the connected signal
      const { conn } = convSignal;
      const connWidth = conn.width();

      // Get the width of the instance (array)
      const instNameResult = schdata.ValidInstanceName.parse(inst.name);
      if (instNameResult.err) return instNameResult; // FIXME: probably handle these errors sooner
      const instName = instNameResult.val;
      const instWidth: number = instName.width();

      // The "total port width" is the product of the instance-array and port widths
      const totalPortWidth = instWidth * pin.width;

      // Closure for adding a connection to the instance
      const connect = (portConn: schdata.Connectable) => {
        inst.conns.push({
          portName: pin.portName,
          conn: portConn,
        });
      };
      // Closure for invalid connections
      const invalid = (): unknown => {
        return this.warnings.push(
          new InvalidPortConnection(inst.name, pin.portName, conn.repr())
        );
      };

      if (totalPortWidth === connWidth) {
        //
        // The easy, normal case.
        // Port width matches signal width.
        // Just connect the signal as it is.
        connect(conn);
        //
      } else if (connWidth === 1) {
        //
        // Width-one connectable
        // Repeat it to match the port width.
        //
        if (conn.nameKind !== "scalar" && conn.nameKind !== "bus") {
          return invalid();
        }

        // OK so `conn` is a `SignalName`. We can repeat it.
        const repeat = schdata.RepeatName.new(totalPortWidth, conn);
        this.conns.addConn(repeat);
        connect(repeat);
        //
      } else {
        //
        // Perhaps there are more valid cases; if so, coming soon.
        // Not totally clear what the rules should be for widths between one and `totalPortWidth`.
        // For now, they all fail.
        //
        return invalid();
      }
    };

    for (const pin of symbol.pins) {
      handleInstancePin(pin);
    }
  };

  // Find the `ConverterSignal` which lands in `bbox`.
  // Returns `null` if none found.
  findSignalHittingBbox = (bbox: schdata.Bbox): ConverterSignal | null => {
    for (const convSignal of this.convSignals.values()) {
      if (convSignal.group.intersectsBbox(bbox)) {
        return convSignal;
      }
    }
    return null;
  };

  // Find the `ConverterSignal` which intersects with `group`.
  // Returns `null` if none found.
  findSignalIntersectingGroup = (
    group: ConnectedGroup
  ): ConverterSignal | null => {
    for (const convSignal of this.convSignals.values()) {
      // Test against its schematic port, if it has one
      const { schPinWrappers } = convSignal;
      for (const schPinWrapper of schPinWrappers) {
        if (group.intersectsPin(schPinWrapper)) {
          return convSignal;
        }
      }

      // Test against its group of wire segments
      if (convSignal.group.intersectsGroup(group)) {
        return convSignal;
      }
    }
    return null; // Nothing hit.
  };

  getOrCreateConvSignal = (connStr: string): Result<ConverterSignal> => {
    // Check for an existing signal, which would've been created for a port
    const existingSig = this.convSignals.get(connStr);
    if (existingSig) return Result.Ok(existingSig);

    // Create a new signal and add this wire to it
    return this.addConn(connStr);
  };

  addConn = (connStr: string): Result<ConverterSignal> => {
    const parseResult = schdata.Connectable.parse(connStr);
    if (parseResult.err) {
      const e = new InvalidSignalName(connStr);
      this.errors.push(e);
      return parseResult;
    }
    const conn = parseResult.val;

    // Add it to our `Conns`
    this.conns.addConn(conn);

    // Create a new `ConverterSignal` for it
    const converterSignal = new ConverterSignal(conn);
    this.convSignals.set(connStr, converterSignal);
    return Result.Ok(converterSignal);
  };

  // Fail, right now, i.e. for stuff from which we can't recover.
  failNow = (msg: any): Result<any> => {
    return Result.Err(msg);
  };
}

// Get the bounding box for an instance port
const instancePinBbox = (inst: schdata.Instance, pin: schdata.SymbolPin) => {
  const { xform } = inst;

  // Get its symbol definition, for its black box
  const pinBbox = schdata.SymbolPin.bbox(pin);
  const bbox = schdata.Xform.transformBbox(xform, pinBbox);

  // Got it. Update the `SchPin` and return the new bbox.
  return bbox;
};

// Test whether `pt` intersects with any of `wire`'s segments.
// FIXME: test non-Manhattan wires
function wireIntersectsPoint(wire: schdata.WireSegment, pt: Point): boolean {
  const manhattanSegments = schdata.Manhattan.calc([...wire.endpoints]);
  if (!manhattanSegments) return false;
  return manhattanSegments.some((seg) => schdata.Manhattan.hitTest(seg, pt, 0));
}

function segmentsIntersect(
  seg1: schdata.WireSegment,
  seg2: schdata.WireSegment
): boolean {
  return (
    wireIntersectsPoint(seg1, seg2.endpoints[0]) ||
    wireIntersectsPoint(seg1, seg2.endpoints[1]) ||
    wireIntersectsPoint(seg2, seg1.endpoints[0]) ||
    wireIntersectsPoint(seg2, seg1.endpoints[1])
  );
}

// Pop a (random) item from a `Set`.
function setPop<T>(set: Set<T>): T | undefined {
  const item = set.values().next().value;
  if (item) set.delete(item);
  return item;
}

class SchPinWrapper {
  constructor(public pinSpec: schdata.SchPinSpec) {
    this.resolvedBbox = schdata.SchPin.calcBbox(pinSpec);
  }
  resolvedBbox: schdata.Bbox;
  contains = (pt: Point): boolean => {
    return schdata.Bbox.contains(this.resolvedBbox, pt);
  };
}

// # Converter Signal
//
// Combination of a `Connectable` and the set of connected wire segments attached to it.
//
class ConverterSignal {
  constructor(public conn: schdata.Connectable) {}

  // Connected group of wire segements
  group: ConnectedGroup = new ConnectedGroup();

  // Optional source schematic-port
  schPinWrappers: Array<SchPinWrapper> = [];
}

// # Connected Group
//
// (of Wire Segments)
//
class ConnectedGroup {
  wireSegments: Set<schdata.WireSegment> = new Set();

  add = (other: ConnectedGroup): void => {
    for (const seg of other.wireSegments) {
      this.wireSegments.add(seg);
    }
  };

  intersectsGroup = (otherGroup: ConnectedGroup): boolean => {
    return Array.from(this.wireSegments).some((ourSeg) =>
      otherGroup.intersectsSegment(ourSeg)
    );
  };
  intersectsSegment = (otherSeg: schdata.WireSegment): boolean => {
    return Array.from(this.wireSegments).some((ourSeg) =>
      segmentsIntersect(ourSeg, otherSeg)
    );
  };
  intersectsPoint = (pt: Point): boolean => {
    return Array.from(this.wireSegments).some((seg) =>
      wireIntersectsPoint(seg, pt)
    );
  };

  // Test for intersection with an `SchPin` via one of the extraction `Wrapper`s.
  // FIXME NOTE: this tests for any *endpoint* inside of the port's bounding box.
  // It does not test for segments passing *through* the port's bounding box, without endpoints inside.
  // This may or may not be the best long-term behavior.
  intersectsPin = (pin: SchPinWrapper): boolean => {
    const { resolvedBbox } = pin;
    return this.intersectsBbox(resolvedBbox);
  };
  intersectsBbox = (bbox: schdata.Bbox): boolean => {
    for (const seg of this.wireSegments) {
      for (const pt of seg.endpoints) {
        if (schdata.Bbox.contains(bbox, pt)) {
          return true;
        }
      }
    }
    return false;
  };
}

// # Error
//
// Stuff that prevents circuit extraction.
// Extraction handles and returns as many of these as it can find.
//
export type ExtractionError =
  | UnboundInstance
  | InvalidSignalName
  | WireConflict;

export class UnboundInstance {
  msg = "Unbound Instance";
  constructor(public instanceName: string, public ident: Ident) {}
}
export class InvalidSignalName {
  msg = "Invalid Signal Name";
  constructor(public name: string) {}
}
export class InvalidPort {
  msg = "Invalid Port";
  constructor(public name: string) {}
}
export class WireConflict {
  msg = "Conflicting labels on wire";
  constructor(public nets: Array<string>) {}
}

// # Warning
//
// Stuff that *doesn't* prevent circuit extraction,
// but usually means the author has made a mistake.
//
export type ExtractionWarning = UnconnectedInstancePort | InvalidPortConnection;

export class UnconnectedInstancePort {
  msg = "Unconnected Instance Port";
  constructor(public instanceName: string, public portName: string) {}
  toString(): string {
    return `${this.msg}: ${this.instanceName}.${this.portName}`;
  }
}
export class InvalidPortConnection {
  msg = "Invalid Instance Connection";
  constructor(
    public instanceName: string,
    public portName: string,
    public conn: string
  ) {}
  toString(): string {
    return `${this.msg}: ${this.instanceName}.${this.portName} -> ${this.conn}`;
  }
}
